// @flow
import * as React from 'react';
import { Device } from '@ta/lithium.context';
import forLabel, { type IAPReason } from './IAPReason';

const contentToServiceInputTypes = {
  ForumPost: 'FORUMPOST',
  Review: 'USERREVIEW',
  ManagementResponse: 'USERREVIEW',
  Photo: 'MEDIA',
  Video: 'MEDIA',
  Trip: '2',
  LinkPost: '1',
  Repost: '3',
  Profile: '4',
  LocationTopic: 'LOCATION_TOPIC',
  LocationPosting: 'LOCATION_POSTING',
};

export type ContentType = $Keys<typeof contentToServiceInputTypes>;

type Submission = {|
  comment: string,
  reason: IAPReason,
  suggestedLocationId?: number,
|};

export type UseSubmitIAPVoteArgs = {|
  contentType: ContentType,
  contentId: string,
  submission: ?Submission,
|};

const fetchUrl = (url: string, securityToken: string) =>
  fetch(url, {
    cache: 'no-cache',
    credentials: 'same-origin',
    method: 'POST',
    redirect: 'follow',
    headers: {
      'X-Requested-By': securityToken,
    },
  });

const legacyInputTypes: Set<$Values<typeof contentToServiceInputTypes>> = new Set([
  'USERREVIEW',
  'MEDIA',
  'LOCATION_TOPIC',
  'LOCATION_POSTING',
]);

const useSubmitIAPVote = ({ contentType, contentId, submission }: UseSubmitIAPVoteArgs) => {
  const { securityToken } = React.useContext(Device);
  return React.useCallback(() => {
    if (!submission) {
      return Promise.reject(new Error('No submission data!'));
    }
    const serviceInputType = contentToServiceInputTypes[contentType];
    const searchParams = new URLSearchParams([
      ['action', 'reportIAP'],
      ['step', 'SAVE'],
      ['contentId', contentId],
      ['voteContentType', serviceInputType],
      ['comment', submission.comment],
      ['reasonId', `${forLabel(submission.reason).id}`],
    ]);
    if (submission.suggestedLocationId) {
      searchParams.set('suggestedLocationId', `${submission.suggestedLocationId}`);
    }

    if (legacyInputTypes.has(serviceInputType)) {
      return fetchUrl(`/ReportIAP?${searchParams.toString()}`, securityToken);
    }
    throw new Error(
      'Only legacy inputs are supported through the Lithium-ported iat-reporting! Feel free to add the remaining ones from public.iat-reporting',
    );
  }, [contentType, contentId, securityToken, submission]);
};

export default useSubmitIAPVote;
